import React, { useState, useEffect } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import {
  useSurveyBuilder,
  SurveyQuestion,
} from "../../../../hooks/useSurveyBuilder";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

const questionTypeLabels: Record<string, string> = {
  rating: "Rating (1-5)",
  nps: "NPS (0-10)",
  yesno: "Yes/No",
  multipleChoice: "Multiple Choice",
  text: "Open Text",
};

const questionTypeIcons: Record<string, string> = {
  rating: "bi-star-half",
  nps: "bi-graph-up-arrow",
  yesno: "bi-toggle-on",
  multipleChoice: "bi-list-ul",
  text: "bi-chat-text",
};

export const SurveyBuilderModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const [activeTab, setActiveTab] = useState("generate");
  const [copied, setCopied] = useState(false);
  const [surveyTitle, setSurveyTitle] = useState("");
  const [surveyDescription, setSurveyDescription] = useState("");

  const {
    isGenerating,
    isLoading,
    isPublishing,
    error,
    generatedSurvey,
    createdSurveyId,
    shareUrl,
    generateQuestions,
    createSurvey,
    publishSurvey,
    updateQuestion,
    removeQuestion,
    clearError,
    reset,
  } = useSurveyBuilder();

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      reset();
      setActiveTab("generate");
      setCopied(false);
      setSurveyTitle("");
      setSurveyDescription("");
    }
  }, [show, reset]);

  // Update local state when survey is generated
  useEffect(() => {
    if (generatedSurvey) {
      setSurveyTitle(generatedSurvey.title);
      setSurveyDescription(generatedSurvey.description);
    }
  }, [generatedSurvey]);

  const handleGenerate = async () => {
    await generateQuestions(projectId, 20);
    setActiveTab("edit");
  };

  const handleCreate = async () => {
    if (!generatedSurvey) return;

    const result = await createSurvey(projectId, {
      ...generatedSurvey,
      title: surveyTitle || generatedSurvey.title,
      description: surveyDescription || generatedSurvey.description,
    });

    if (result) {
      setActiveTab("publish");
    }
  };

  const handlePublish = async () => {
    if (!createdSurveyId) return;

    await publishSurvey(projectId, createdSurveyId);
    setActiveTab("live");
  };

  const handleCopyUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenUrl = () => {
    if (shareUrl) {
      window.open(shareUrl, "_blank");
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bolder">
          <i className="bi bi-clipboard2-data me-2 text-primary"></i>
          Survey Builder
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="flex-grow-1">{error}</span>
            <button
              type="button"
              className="btn-close"
              onClick={clearError}
            ></button>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mb-4"
        >
          {/* Generate Tab */}
          <Tab eventKey="generate" title="1. Generate">
            <div className="py-4">
              <div className="text-center mb-5">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-light-primary rounded-circle mb-4"
                  style={{ width: "80px", height: "80px" }}
                >
                  <i className="bi bi-magic text-primary fs-1"></i>
                </div>
                <h4 className="fw-bold mb-2">Generate Validation Survey</h4>
                <p className="text-muted mb-0 mx-auto" style={{ maxWidth: 500 }}>
                  AI will analyze your Step 20 assumptions and create targeted
                  questions to validate your business hypotheses.
                </p>
              </div>

              <div className="bg-light-primary rounded p-4 mb-4">
                <h6 className="fw-bold text-primary mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  What will be generated
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Rating questions (1-5 scale)
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        NPS questions (0-10)
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Yes/No validation questions
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Multiple choice scenarios
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Open-ended feedback
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Linked to your assumptions
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="alert alert-warning">
                <i className="bi bi-lightbulb me-2"></i>
                <strong>Tip:</strong> Make sure you've completed Step 20 (Key Assumptions)
                before generating your survey.
              </div>
            </div>
          </Tab>

          {/* Edit Tab */}
          <Tab
            eventKey="edit"
            title="2. Edit"
            disabled={!generatedSurvey}
          >
            {generatedSurvey && (
              <div className="py-4">
                {/* Survey Title & Description */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Survey Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={surveyTitle}
                      onChange={(e) => setSurveyTitle(e.target.value)}
                      placeholder="Enter survey title"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={surveyDescription}
                      onChange={(e) => setSurveyDescription(e.target.value)}
                      placeholder="Brief description for respondents"
                    />
                  </div>
                </div>

                {/* Questions List */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">
                      Questions ({generatedSurvey.questions.length})
                    </h6>
                  </div>

                  <div className="border rounded">
                    {generatedSurvey.questions.map((question, index) => (
                      <QuestionEditor
                        key={question.id}
                        question={question}
                        index={index}
                        onUpdate={(updates) =>
                          updateQuestion(question.id, updates)
                        }
                        onRemove={() => removeQuestion(question.id)}
                        isLast={
                          index === generatedSurvey.questions.length - 1
                        }
                      />
                    ))}
                  </div>
                </div>

                {/* Settings */}
                <div className="bg-light rounded p-4">
                  <h6 className="fw-bold mb-3">Settings</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="allowAnonymous"
                          defaultChecked
                        />
                        <label
                          className="form-check-label"
                          htmlFor="allowAnonymous"
                        >
                          Allow anonymous responses
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="requireEmail"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="requireEmail"
                        >
                          Require email address
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Tab>

          {/* Publish Tab */}
          <Tab
            eventKey="publish"
            title="3. Publish"
            disabled={!createdSurveyId}
          >
            {createdSurveyId && (
              <div className="py-5 text-center">
                <div className="mb-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center bg-light-success rounded-circle mb-4"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <i className="bi bi-file-check text-success fs-1"></i>
                  </div>
                  <h4 className="fw-bold mb-2">Survey Created!</h4>
                  <p className="text-muted">
                    Your survey is saved as a draft. Publish it to start
                    collecting responses.
                  </p>
                </div>

                <div className="bg-light rounded p-4 mb-4 d-inline-block">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="badge bg-warning text-dark">Draft</span>
                    <span className="text-muted">
                      Survey ID: {createdSurveyId}
                    </span>
                  </div>
                  <p className="text-muted mb-0 small">
                    Publishing will make your survey publicly accessible.
                    Responses will be collected and analyzed automatically.
                  </p>
                </div>
              </div>
            )}
          </Tab>

          {/* Live Tab */}
          <Tab eventKey="live" title="4. Live" disabled={!shareUrl}>
            {shareUrl && (
              <div className="py-5 text-center">
                <div className="mb-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center bg-success rounded-circle mb-4"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <i className="bi bi-broadcast text-white fs-1"></i>
                  </div>
                  <h3 className="fw-bold mb-2">Your survey is live!</h3>
                  <p className="text-muted">
                    Share the link below to start collecting responses.
                  </p>
                </div>

                <div className="bg-light rounded p-4 mb-4 d-inline-block">
                  <div className="d-flex align-items-center gap-3">
                    <code className="fs-5 text-primary">{shareUrl}</code>
                    <button
                      className="btn btn-sm btn-light-primary"
                      onClick={handleCopyUrl}
                    >
                      {copied ? (
                        <i className="bi bi-check2"></i>
                      ) : (
                        <i className="bi bi-clipboard"></i>
                      )}
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleOpenUrl}
                    >
                      <i className="bi bi-box-arrow-up-right me-1"></i>
                      Preview
                    </button>
                  </div>
                </div>

                <div className="text-muted fs-7">
                  <p className="mb-1">
                    <i className="bi bi-graph-up me-2"></i>
                    Responses will auto-populate Step 21
                  </p>
                  <p className="mb-0">
                    <i className="bi bi-bar-chart me-2"></i>
                    View analytics in your project dashboard
                  </p>
                </div>
              </div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button
          type="button"
          className="btn btn-light"
          onClick={handleClose}
          disabled={isGenerating || isLoading || isPublishing}
        >
          {shareUrl ? "Done" : "Cancel"}
        </button>

        {activeTab === "generate" && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Generating...
              </>
            ) : (
              <>
                <i className="bi bi-magic me-2"></i>
                Generate Survey
              </>
            )}
          </button>
        )}

        {activeTab === "edit" && generatedSurvey && (
          <>
            <button
              type="button"
              className="btn btn-light-primary"
              onClick={() => {
                reset();
                setActiveTab("generate");
              }}
              disabled={isLoading}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Regenerate
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={isLoading || generatedSurvey.questions.length === 0}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  Save Survey
                </>
              )}
            </button>
          </>
        )}

        {activeTab === "publish" && createdSurveyId && (
          <button
            type="button"
            className="btn btn-success"
            onClick={handlePublish}
            disabled={isPublishing}
          >
            {isPublishing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Publishing...
              </>
            ) : (
              <>
                <i className="bi bi-broadcast me-2"></i>
                Publish Survey
              </>
            )}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// Question Editor Component
interface QuestionEditorProps {
  question: SurveyQuestion;
  index: number;
  onUpdate: (updates: Partial<SurveyQuestion>) => void;
  onRemove: () => void;
  isLast: boolean;
}

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  question,
  index,
  onUpdate,
  onRemove,
  isLast,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(question.text);

  const handleSave = () => {
    onUpdate({ text: editedText });
    setIsEditing(false);
  };

  return (
    <div
      className={`p-3 ${!isLast ? "border-bottom" : ""}`}
      style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f9fafb" }}
    >
      <div className="d-flex align-items-start gap-3">
        {/* Question Number & Type */}
        <div className="text-center" style={{ minWidth: 60 }}>
          <span className="badge bg-primary rounded-circle fs-6 mb-1">
            {index + 1}
          </span>
          <div className="text-muted small">
            <i className={`bi ${questionTypeIcons[question.type]} me-1`}></i>
            {question.type}
          </div>
        </div>

        {/* Question Content */}
        <div className="flex-grow-1">
          {isEditing ? (
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control form-control-sm"
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                autoFocus
              />
              <button
                className="btn btn-sm btn-success"
                onClick={handleSave}
              >
                <i className="bi bi-check"></i>
              </button>
              <button
                className="btn btn-sm btn-light"
                onClick={() => {
                  setEditedText(question.text);
                  setIsEditing(false);
                }}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          ) : (
            <div>
              <p className="mb-1 fw-medium">{question.text}</p>
              {question.linkedAssumption && (
                <span className="badge bg-light-primary text-primary small">
                  Validates Assumption {question.linkedAssumption}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="d-flex gap-1">
            <button
              className="btn btn-sm btn-light"
              onClick={() => setIsEditing(true)}
              title="Edit"
            >
              <i className="bi bi-pencil"></i>
            </button>
            <button
              className="btn btn-sm btn-light-danger"
              onClick={onRemove}
              title="Remove"
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyBuilderModal;
